/**
 * Candidate Generator Utility
 *
 * Generates realistic candidate profiles based on recruitment source and party ideology.
 * Each source produces candidates with distinct archetypes and stat distributions.
 *
 * Generation process:
 * 1. Select archetype based on recruitment source
 * 2. Generate stats using base + variance from archetype
 * 3. Generate ideology from party position + variance + bias
 * 4. Generate name and age appropriate to source
 */

import type { IdeologyVector, RecruitmentSource } from '@/types'
import { IDEOLOGY_AXES, IDEOLOGY_MIN, IDEOLOGY_MAX } from '@/constants/ideology'
import { SOURCE_ARCHETYPES, type StatModifier } from '@/constants/recruitmentSources'
import { generateUniqueName, generateAge } from './nameGenerator'

/**
 * Input for generating a candidate.
 */
export interface CandidateGeneratorInput {
  partyId: string
  partyIdeology: IdeologyVector
  recruitmentSource: RecruitmentSource
  currentTick: number
  existingNames?: string[]
}

/**
 * A generated candidate ready for insertion into the database.
 * Does not include id (generated by DB) or traits (empty by default).
 */
export interface GeneratedCandidate {
  party_id: string
  name: string
  age: number
  ideology_vector: IdeologyVector
  loyalty: number
  recognition: number
  charisma: number
  admin_skill: number
  debate_skill: number
  scandal_prone: number
  ambition: number
  recruitment_source: RecruitmentSource
  recruited_tick: number
  role: 'none'
  is_active: true
}

/**
 * Generate a stat value using a normal-ish distribution around the base.
 * Uses the average of two random numbers to create a bell curve tendency.
 *
 * @param base - Center value for the distribution
 * @param variance - How much the value can deviate from base
 * @returns A stat value clamped to 0-100
 */
export function generateStat(base: number, variance: number): number {
  // Average of 2 randoms creates triangular distribution centered at 0.5
  const r1 = Math.random()
  const r2 = Math.random()
  const normalizedRandom = (r1 + r2) / 2

  // Map from [0, 1] to [-1, 1] for symmetric deviation
  const deviation = (normalizedRandom - 0.5) * 2

  // Apply variance (can go up or down from base)
  const value = base + deviation * variance

  // Clamp to valid stat range
  return Math.round(Math.max(0, Math.min(100, value)))
}

/**
 * Generate a candidate's ideology based on party ideology with variance and bias.
 *
 * @param partyIdeology - The party's ideology vector
 * @param variance - How much each axis can deviate from party position
 * @param bias - Optional bias to apply to specific axes (added after variance)
 * @returns A new ideology vector for the candidate
 */
export function generateCandidateIdeology(
  partyIdeology: IdeologyVector,
  variance: number,
  bias?: Partial<Record<string, number>>
): IdeologyVector {
  const result: number[] = []

  for (let i = 0; i < 7; i++) {
    const axisKey = IDEOLOGY_AXES[i].key
    const partyValue = partyIdeology[i]

    // Generate random deviation within variance using triangular distribution
    const r1 = Math.random()
    const r2 = Math.random()
    const normalizedRandom = (r1 + r2) / 2
    const deviation = (normalizedRandom - 0.5) * 2 * variance

    // Start with party value plus random deviation
    let value = partyValue + deviation

    // Apply bias if specified for this axis
    if (bias && axisKey in bias) {
      value += bias[axisKey]!
    }

    // Clamp to valid ideology range
    result.push(Math.round(Math.max(IDEOLOGY_MIN, Math.min(IDEOLOGY_MAX, value))))
  }

  return result as IdeologyVector
}

/**
 * Generate all stats for a candidate from stat modifiers.
 */
function generateStats(modifiers: Record<string, StatModifier>): {
  loyalty: number
  recognition: number
  charisma: number
  admin_skill: number
  debate_skill: number
  scandal_prone: number
  ambition: number
} {
  return {
    loyalty: generateStat(modifiers.loyalty.base, modifiers.loyalty.variance),
    recognition: generateStat(modifiers.recognition.base, modifiers.recognition.variance),
    charisma: generateStat(modifiers.charisma.base, modifiers.charisma.variance),
    admin_skill: generateStat(modifiers.admin_skill.base, modifiers.admin_skill.variance),
    debate_skill: generateStat(modifiers.debate_skill.base, modifiers.debate_skill.variance),
    scandal_prone: generateStat(
      modifiers.scandal_prone.base,
      modifiers.scandal_prone.variance
    ),
    ambition: generateStat(modifiers.ambition.base, modifiers.ambition.variance),
  }
}

/**
 * Generate a single candidate based on input parameters.
 *
 * @param input - Generation parameters including party, source, and tick
 * @returns A fully generated candidate ready for database insertion
 *
 * @example
 * const candidate = generateCandidate({
 *   partyId: 'abc-123',
 *   partyIdeology: [20, 40, 30, 10, 50, 0, 15],
 *   recruitmentSource: 'youth_wing',
 *   currentTick: 5,
 * });
 */
export function generateCandidate(input: CandidateGeneratorInput): GeneratedCandidate {
  const { partyId, partyIdeology, recruitmentSource, currentTick, existingNames = [] } = input

  const archetype = SOURCE_ARCHETYPES[recruitmentSource]

  // Generate unique name
  const name = generateUniqueName(existingNames)

  // Generate age appropriate to recruitment source
  const age = generateAge(recruitmentSource)

  // Generate stats based on archetype
  const stats = generateStats(archetype.statModifiers)

  // Generate ideology with variance and bias from archetype
  const ideology = generateCandidateIdeology(
    partyIdeology,
    archetype.ideologyVariance,
    archetype.ideologyBias
  )

  return {
    party_id: partyId,
    name,
    age,
    ideology_vector: ideology,
    ...stats,
    recruitment_source: recruitmentSource,
    recruited_tick: currentTick,
    role: 'none',
    is_active: true,
  }
}

/**
 * Generate multiple candidate options for scouting (player picks one).
 * All candidates share the same recruitment source but have different stats.
 *
 * @param input - Generation parameters (existingNames not used, names are unique within batch)
 * @param count - Number of candidates to generate (default 3)
 * @returns Array of generated candidates
 *
 * @example
 * const options = generateCandidateOptions({
 *   partyId: 'abc-123',
 *   partyIdeology: [20, 40, 30, 10, 50, 0, 15],
 *   recruitmentSource: 'youth_wing',
 *   currentTick: 5,
 * }, 3);
 * // Returns 3 unique candidates with youth_wing characteristics
 * // Player will pick one in the UI
 */
export function generateCandidateOptions(
  input: Omit<CandidateGeneratorInput, 'existingNames'>,
  count: number = 3
): GeneratedCandidate[] {
  const candidates: GeneratedCandidate[] = []
  const generatedNames: string[] = []

  for (let i = 0; i < count; i++) {
    const candidate = generateCandidate({
      ...input,
      existingNames: generatedNames,
    })
    candidates.push(candidate)
    generatedNames.push(candidate.name)
  }

  return candidates
}
